import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  InputGroup,
  Form,
  Pagination,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetStatsDataQuery,
  useToggleStatsStatusMutation,
  useDeleteStatsMutation,
} from "../../../redux/api/StatsApi/statsApi";
import DeleteModel from "../../../Components/DeleteModel";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaChartBar,
  FaCheckCircle,
  FaTimesCircle,
  FaTrashAlt,
} from "react-icons/fa";
import "../Services/Services.css";

const Stats = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "display_order";
  const order = searchParams.get("order") || "asc";
  const limit = Number(searchParams.get("limit")) || 10;

  const [searchTerm, setSearchTerm] = useState(search);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStat, setSelectedStat] = useState(null);

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  // Debounced search update
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== search) {
        setSearchParams({
          page: 1,
          limit,
          search: searchTerm,
          sortBy,
          order,
        });
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm, limit, sortBy, order, search, setSearchParams]);

  const { data, isLoading, isFetching } = useGetStatsDataQuery({
    page,
    limit,
    search,
    sortBy,
    order,
  });

  const [toggleStatus] = useToggleStatsStatusMutation();
  const [deleteStats] = useDeleteStatsMutation();

  const stats = data?.stats || [];
  const totalPages = Math.ceil((data?.total || 0) / limit);

  const handleToggleStatus = async (id) => {
    try {
      await toggleStatus(id).unwrap();
      message.success("Status updated successfully!");
    } catch (error) {
      console.error("Failed to toggle status:", error);
      message.error("Failed to update status.");
    }
  };

  const handleDelete = (stat) => {
    setSelectedStat(stat);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteStats(selectedStat.id).unwrap();
      setShowDeleteModal(false);
      setSelectedStat(null);
      message.success("Stats deleted successfully!");
    } catch (error) {
      console.error("Failed to delete stats:", error);
      message.error("Failed to delete stats.");
    }
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSelectedStat(null);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaChartBar className="text-primary me-2" />
            Stats Management
          </h2>
          <p className="text-muted mb-0">
            Manage your company statistics and strengths
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate("/admin/add-stats")}
          className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm rounded-pill fw-semibold"
        >
          <FaPlus /> Add New Stats
        </Button>
      </div>

      {/* Table Section */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
        <Card.Header className="bg-white border-bottom p-3">
          <Row className="align-items-center">
            <Col md={3} className="d-none d-md-block">
              <h5 className="mb-0 fw-bold px-2">Stats List</h5>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                className="w-auto cursor-pointer"
                value={limit}
                onChange={(e) =>
                  setSearchParams({
                    page: 1,
                    limit: Number(e.target.value),
                    search,
                    sortBy,
                    order,
                  })
                }
              >
                <option value={10}>Show 10</option>
                <option value={25}>Show 25</option>
                <option value={50}>Show 50</option>
                <option value={100}>Show 100</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <InputGroup className="bg-light rounded-pill border-0 overflow-hidden px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search stats..."
                  className="bg-transparent border-0 shadow-none py-2"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              {isFetching && (
                <div className="text-end text-muted extra-small mt-1 px-3">
                  Updating results...
                </div>
              )}
            </Col>
          </Row>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4 border-0">S.No</th>
                <th className="border-0">Title</th>
                <th className="border-0">Overview</th>
                <th className="text-center border-0">Values</th>
                <th className="text-center border-0">Status</th>
                <th className="text-end pe-4 border-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats && stats.length > 0 ? (
                stats.map((s, i) => (
                  <tr key={s.id}>
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark">
                        {(page - 1) * limit + i + 1}
                      </div>
                    </td>
                    <td>
                      <div className="fw-bold text-dark">{s.title}</div>
                      <div
                        className="text-muted small truncate-2"
                        title={s.description}
                        style={{ maxWidth: "200px" }}
                      >
                        {s.description}
                      </div>
                    </td>
                    <td>
                      <div className="extra-small text-muted">
                        Order: {s.display_order}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="d-flex flex-wrap justify-content-center gap-1">
                        <Badge bg="info-light" className="text-info border-0">
                          {s.stat_one_value} {s.stat_one_title}
                        </Badge>
                        <Badge bg="info-light" className="text-info border-0">
                          {s.stat_two_value} {s.stat_two_title}
                        </Badge>
                      </div>
                    </td>
                    <td className="text-center">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleToggleStatus(s.id)}
                      >
                        {s.status ? (
                          <Badge
                            bg="success-light"
                            className="text-success border-0 px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1"
                          >
                            <FaCheckCircle size={10} /> Active
                          </Badge>
                        ) : (
                          <Badge
                            bg="danger-light"
                            className="text-danger border-0 px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1"
                          >
                            <FaTimesCircle size={10} /> Inactive
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-end pe-4">
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() => navigate(`/admin/edit-stats/${s.id}`)}
                          className="btn-action shadow-sm border"
                          title="Edit Stats"
                        >
                          <FaEdit className="text-primary" />
                        </Button>
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() => handleDelete(s)}
                          className="btn-action shadow-sm border"
                          title="Delete Stats"
                        >
                          <FaTrashAlt className="text-danger" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <div className="py-4">
                      <FaChartBar size={48} className="text-light mb-3" />
                      <h5 className="text-muted fw-bold">No Stats Found</h5>
                      <p className="text-muted small">
                        {search
                          ? `No results for "${search}"`
                          : "Create your first statistic collection."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Pagination UI */}
      {totalPages > 1 && (
        <Pagination className="justify-content-center my-4 custom-pagination">
          <Pagination.Prev
            disabled={page === 1}
            onClick={() =>
              setSearchParams({
                page: page - 1,
                limit,
                search,
                sortBy,
                order,
              })
            }
          />

          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={page === i + 1}
              onClick={() =>
                setSearchParams({
                  page: i + 1,
                  limit,
                  search,
                  sortBy,
                  order,
                })
              }
            >
              {i + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            disabled={page === totalPages}
            onClick={() =>
              setSearchParams({
                page: page + 1,
                limit,
                search,
                sortBy,
                order,
              })
            }
          />
        </Pagination>
      )}

      <DeleteModel
        DELETESTATE={showDeleteModal}
        ONCLICK={handleCloseModal}
        YES={handleDeleteConfirm}
        DELETETITLE="Delete Stats"
        DESCRIPTION={`Are you sure you want to delete this statistic entry?`}
        loadTest="Deleting..."
      />
    </div>
  );
};

export default Stats;
